/**
 * Reducer helpers for attribute-related commands:
 *   - geometry/create-attdef          (Phase 2.1 — ATTDEF authoring)
 *   - geometry/update-attribute-value (DDATTE / ATTEDIT)
 *   - geometry/update-attribute-values(EATTEDIT Apply/OK)
 *   - geometry/remove-attdef          (Phase 2.4 BATTMAN)
 *   - geometry/update-attdef          (Phase 2.4 BATTMAN)
 *   - geometry/rename-attdef-tag      (Phase 2.4 BATTMAN)
 *   - geometry/reorder-attdefs        (Phase 2.4 BATTMAN)
 *   - geometry/attsync                (Phase 2.5 ATTSYNC)
 *
 * Split out of geometryReducer.ts so that file stays below the
 * 2000-line soft limit (CLAUDE.md). All helpers take the pre-validated
 * EditorCommand union member (matched by the reducer `switch`) and
 * return the updated document — if the command is a no-op (e.g.
 * create-attdef outside of BEDIT) the document is returned unchanged.
 */

import {
  assembleDrawingDocument,
  assembleDrawingDocumentInPlace,
  coerceColor,
  type AttdefEntity,
  type BlockDefinition,
  type BlockInsertEntity,
  type DrawingDocument,
  type EntityColor,
} from "@dxf-ai-editor/drawing-model";
import {
  createAttdefMutation,
  updateAttributeValueMutation,
  updateAttributeValuesMutation,
  removeAttdefMutation,
  updateAttdefMutation,
  renameAttdefTagMutation,
  reorderAttdefsMutation,
  attsyncMutation,
} from "@dxf-ai-editor/command-engine";
import type { EditorCommand } from "@dxf-ai-editor/contracts";

type CreateAttdefCommand = Extract<
  EditorCommand,
  { type: "geometry/create-attdef" }
>;
type UpdateAttributeValueCommand = Extract<
  EditorCommand,
  { type: "geometry/update-attribute-value" }
>;
type UpdateAttributeValuesCommand = Extract<
  EditorCommand,
  { type: "geometry/update-attribute-values" }
>;
type RemoveAttdefCommand = Extract<
  EditorCommand,
  { type: "geometry/remove-attdef" }
>;
type UpdateAttdefCommand = Extract<
  EditorCommand,
  { type: "geometry/update-attdef" }
>;
type RenameAttdefTagCommand = Extract<
  EditorCommand,
  { type: "geometry/rename-attdef-tag" }
>;
type ReorderAttdefsCommand = Extract<
  EditorCommand,
  { type: "geometry/reorder-attdefs" }
>;
type AttsyncCommand = Extract<EditorCommand, { type: "geometry/attsync" }>;

// ═══════════════════════════════════════════════════════════════
// create-attdef
// ═══════════════════════════════════════════════════════════════

/**
 * Allocate an id for a fresh ATTDEF. Matches the nextGeometryEntityId
 * pattern in geometryReducer.ts but scans blockDef.entities as well —
 * ATTDEFs live only inside BlockDefinition.entities.
 */
function nextAttdefId(document: DrawingDocument, blockName: string): string {
  const blockDef = document.blocks[blockName];
  const attdefTags = new Set<string>();
  if (blockDef) {
    for (const child of blockDef.entities) {
      if (child.type === "Attdef") attdefTags.add(child.id);
    }
  }
  // Also scan space entities — block-edit mirror puts ATTDEF-like
  // entries under BLOCK_EDIT_SPACE_ID during authoring so we need to
  // avoid collisions there too. Defensive: blockEditReducer currently
  // strips ATTDEFs from the canvas, but keep this check in case the
  // mirror is extended in a later phase.
  for (const space of document.spaces) {
    for (const entity of space.entities) {
      attdefTags.add(entity.id);
    }
  }
  let counter = attdefTags.size + 1;
  let candidate = `attdef-${counter}`;
  while (attdefTags.has(candidate)) {
    counter += 1;
    candidate = `attdef-${counter}`;
  }
  return candidate;
}

export function handleCreateAttdef(
  document: DrawingDocument,
  command: CreateAttdefCommand,
): DrawingDocument {
  // CECOLOR round-6 FINDING B (P2 redo) — REDO replay path.
  // The genuine user authoring path below is BEDIT-gated (AutoCAD requires
  // an active block-edit session to author an ATTDEF). But UNDO removes the
  // ATTDEF session-independently (via `geometry/remove-attdef` →
  // removeAttdefMutation, which does NOT require an active BEDIT space), so
  // the inverse can be undone AFTER the user has exited BEDIT. REDO must be
  // symmetric: re-adding the ATTDEF must ALSO succeed with no active session,
  // otherwise the redo silently no-ops and the future slot is consumed →
  // the ATTDEF is permanently lost. The dispatch chokepoint records the
  // already-materialized AttdefEntity on the history `forward` as an internal
  // `__replayAttdef` field; honour it here by inserting that exact entity
  // (same id / colour / geometry) directly into the block definition,
  // bypassing the gate. Idempotent — a duplicate id is left untouched.
  const replayAttdef = (
    command as CreateAttdefCommand & { __replayAttdef?: AttdefEntity }
  ).__replayAttdef;
  if (replayAttdef) {
    const blockDefForReplay = document.blocks[command.blockName];
    if (!blockDefForReplay) {
      return document;
    }
    if (blockDefForReplay.entities.some((e) => e.id === replayAttdef.id)) {
      return document; // already present — idempotent replay no-op
    }
    return assembleDrawingDocument({
      ...document,
      blocks: {
        ...document.blocks,
        [command.blockName]: {
          ...blockDefForReplay,
          entities: [...blockDefForReplay.entities, replayAttdef],
        },
      },
    });
  }

  // Enforce: must be inside BEDIT and target block must match.
  const blockEditSpace = document.spaces.find((s) => s.kind === "block-edit");
  if (!blockEditSpace || blockEditSpace.blockEditName !== command.blockName) {
    // Silent no-op — App.tsx surfaces the toast.
    return document;
  }
  const blockDef = document.blocks[command.blockName];
  if (!blockDef) {
    return document;
  }

  let attdefEntity: AttdefEntity;
  try {
    const result = createAttdefMutation({
      nextId: nextAttdefId(document, command.blockName),
      spaceId: blockEditSpace.id,
      blockName: command.blockName,
      attdef: {
        tag: command.attdef.tag,
        prompt: command.attdef.prompt,
        defaultValue: command.attdef.defaultValue,
        insertionPoint: {
          x: command.attdef.insertionPoint.x,
          y: command.attdef.insertionPoint.y,
        },
        height: command.attdef.height,
        rotation: command.attdef.rotation,
        widthFactor: command.attdef.widthFactor,
        obliqueAngle: command.attdef.obliqueAngle,
        styleName: command.attdef.styleName,
        hJustification: command.attdef.hJustification,
        vJustification: command.attdef.vJustification,
        ...(command.attdef.alignmentPoint
          ? {
              alignmentPoint: {
                x: command.attdef.alignmentPoint.x,
                y: command.attdef.alignmentPoint.y,
              },
            }
          : {}),
        flags: command.attdef.flags,
        backgroundFill: command.attdef.backgroundFill,
        ...(command.attdef.backgroundColor !== undefined
          ? { backgroundColor: command.attdef.backgroundColor }
          : {}),
        fillBoxScale: command.attdef.fillBoxScale,
      },
      // T7 (agent-bridge) local Codex round-16 P2 — `command.layer` is now
      // OPTIONAL (dispatch-chokepoint-stamped CLAYER, mirroring `color`
      // below); fall back to "0" for a direct/test caller that bypasses the
      // stamp entirely (mirrors `color`'s own undefined-tolerant handling —
      // `createAttdefMutation` coerces an undefined color, but `layer` on
      // `AttdefEntity` is a plain required string).
      layer: command.layer ?? "0",
      color: command.color,
      linetype: command.linetype,
      lineweight: command.lineweight,
    });
    attdefEntity = result.attdefEntity;
  } catch {
    // Validation error — App.tsx surfaces the toast; reducer leaves the
    // document untouched so the user can correct the dialog inputs.
    return document;
  }

  // Insert into blocks[blockName].entities. The block-edit mirror in
  // blockEditReducer.syncBlockEditSpaceToDefinition runs on the next
  // reduce pass, but since ATTDEFs live outside the space's entity
  // array (the canvas strips them on hydrate) we must update both the
  // blockDef here AND let the mirror preserve them on subsequent edits.
  const nextBlockDef: BlockDefinition = {
    ...blockDef,
    entities: [...blockDef.entities, attdefEntity],
  };
  return assembleDrawingDocument({
    ...document,
    blocks: {
      ...document.blocks,
      [command.blockName]: nextBlockDef,
    },
  });
}

// ═══════════════════════════════════════════════════════════════
// update-attribute-value / update-attribute-values
// ═══════════════════════════════════════════════════════════════

/**
 * Find an INSERT by id across all spaces. Returns null if no INSERT
 * with that id exists (the command is then a silent no-op).
 *
 * Exported (Lane F round-? FINDING 2) — the store's undo-inverse builder
 * (`editorStore.ts`) needs the SAME lookup to snapshot the pre/post INSERT
 * around a DDATTE/EATTEDIT dispatch; reusing this helper instead of a
 * second copy keeps ONE lookup truth (CLAUDE.md dup:check).
 */
export function findInsertById(
  document: DrawingDocument,
  insertId: string,
): BlockInsertEntity | null {
  for (const space of document.spaces) {
    const candidate = space.entities.find((e) => e.id === insertId);
    if (candidate && candidate.type === "Block") return candidate;
  }
  return null;
}

/**
 * Locate an INSERT nested INSIDE a block definition (`blocks[name].entities`
 * — `blockInsertEntitySchema` is a valid `blockChildEntitySchema` member, so
 * one block can reference another). Round-3 FINDING 2 — the restore path
 * below (`restoreInsertAttributesVerbatim`) needs this as a fallback for a
 * nested INSERT that was edited while its OWNING block was under BEDIT: the
 * synthetic block-edit space mirrors `blocks[blockEditName].entities`
 * (`blockEditReducer.ts` module doc), so `findInsertById` finds the nested
 * INSERT there WHILE BEDIT is active — but once the user saves/exits BEDIT
 * the synthetic space is torn down (`exitBlockEditSpace`) and the edited
 * INSERT survives ONLY as a block-definition child, invisible to
 * `findInsertById`'s space-only scan.
 */
function findInsertInBlockDefinitions(
  document: DrawingDocument,
  insertId: string,
): { insert: BlockInsertEntity; blockName: string } | null {
  for (const [blockName, blockDef] of Object.entries(document.blocks)) {
    const candidate = blockDef.entities.find((e) => e.id === insertId);
    if (candidate && candidate.type === "Block") {
      return { insert: candidate, blockName };
    }
  }
  return null;
}

/**
 * Round-4 FINDING 2 — resolve the INSERT with `insertId` in EITHER a live
 * space OR (a nested INSERT whose owning BEDIT session has since been
 * saved/exited — module doc on `findInsertInBlockDefinitions`) a block
 * definition. Shared lookup twin of `patchInsertAnywhere` below: this
 * resolves the entity to READ (for `updateAttributeValue(s)Mutation`'s
 * `insert`/`blockDefinition` inputs); `patchInsertAnywhere` resolves the
 * SAME location to WRITE the result back to. Used by the ordinary forward
 * DDATTE/EATTEDIT handlers so a REDO of `geometry/update-attribute-value(s)`
 * after BEDIT has been exited does not silently no-op (the space-only
 * `findInsertById` alone cannot see a target that has moved into
 * `blocks[name].entities`).
 *
 * Round-5 FINDING 2 — also exported for the undo-inverse BUILDER in
 * `editorStore.ts`: it snapshots the pre/post INSERT to decide WHICH inverse
 * to record, and the space-only `findInsertById` returned `null` for a nested
 * INSERT whose BEDIT space had been saved/closed — so a successful forward
 * ATTRIB edit produced NO history entry at all (undo silently impossible).
 * Using this shared block-definition-aware lookup there makes the snapshot see
 * the SAME target the forward mutation + restore path already resolve.
 */
export function resolveInsertAnywhere(
  document: DrawingDocument,
  insertId: string,
): BlockInsertEntity | null {
  const spaceInsert = findInsertById(document, insertId);
  if (spaceInsert) return spaceInsert;
  return findInsertInBlockDefinitions(document, insertId)?.insert ?? null;
}

/**
 * Locate the INSERT with `insertId` in either a live space OR (round-4
 * FINDING 2 — nested inside a block definition, its owning BEDIT session
 * since saved/exited) a block definition, and apply `buildPatch` to it in
 * place. Mirrors `restoreInsertAttributesVerbatim`'s (round-3 FINDING 2)
 * two-branch resolution — extracted so BOTH the undo-inverse verbatim
 * restore AND the ordinary forward DDATTE/EATTEDIT/REDO mutation path share
 * ONE "resolve + patch an INSERT's attribute-bearing fields" truth
 * (CLAUDE.md dup:check — the two branches were otherwise byte-identical
 * except for the patch payload). Returns `document` unchanged ONLY when the
 * INSERT is not found in EITHER location (e.g. the block was itself deleted
 * after the edit — a genuinely irrecoverable target).
 */
function patchInsertAnywhere(
  document: DrawingDocument,
  insertId: string,
  buildPatch: (
    entity: Extract<BlockInsertEntity, { type: "Block" }>,
  ) => Partial<BlockInsertEntity>,
): DrawingDocument {
  if (findInsertById(document, insertId)) {
    return patchInsertEntityInPlace(document, insertId, buildPatch);
  }

  const located = findInsertInBlockDefinitions(document, insertId);
  if (!located) return document;
  const blockDef = document.blocks[located.blockName];
  if (!blockDef) return document;

  return assembleDrawingDocument({
    ...document,
    blocks: {
      ...document.blocks,
      [located.blockName]: {
        ...blockDef,
        entities: blockDef.entities.map((child) => {
          if (child.id !== insertId || child.type !== "Block") return child;
          return { ...child, ...buildPatch(child) };
        }),
      },
    },
  });
}

/**
 * Verbatim full-snapshot restore for the undo inverse of
 * `geometry/update-attribute-value(s)` (Lane F round-? FINDING 2). Writes
 * `attributes[]` + the four EATTEDIT-editable common fields EXACTLY as
 * captured pre-mutation — no re-run of the tag-matching
 * `updateAttributeValue(s)Mutation`, and no colour-string round-trip
 * (`patchInsertAttributes`'s `properties.color` path runs the captured
 * value through `coerceColor(string)`, which cannot represent a rich
 * colour-book/true-colour-with-name value losslessly — the typed
 * `EntityColor` snapshot is written directly here instead).
 *
 * A raw array replace (rather than a tag-keyed `updates` patch) is required
 * for full-fidelity undo: `updateAttributeValueMutation` can only
 * update-or-create a tag, never REMOVE one, so a forward that materialized
 * a fresh ATTRIB entry (a tag present on the ATTDEF template but absent
 * from this INSERT's `attributes[]` until edited) could never be undone by
 * replaying updates — the created row would survive undo. Replacing the
 * whole array with the pre-mutation snapshot omits that tag entirely,
 * exactly reversing the create.
 *
 * Round-3 FINDING 2 — tries the ordinary space lookup first (the common
 * case), then falls back to a nested block-definition INSERT via
 * `patchInsertAnywhere` (round-4 — extracted, see its own doc) for a nested
 * INSERT whose owning BEDIT session has since been saved/exited (module doc
 * on `findInsertInBlockDefinitions`). Without the fallback the restore
 * silently no-op'd on an INSERT that exists only inside
 * `blocks[name].entities` — the document came back unchanged, but the
 * history slot was still CONSUMED, so the next Ctrl+Z undid an unrelated,
 * earlier edit instead.
 */
function restoreInsertAttributesVerbatim(
  document: DrawingDocument,
  insertId: string,
  snapshot: RestoreAttributesSnapshot,
): DrawingDocument {
  return patchInsertAnywhere(document, insertId, () => ({
    attributes: snapshot.attributes,
    layer: snapshot.layer,
    color: snapshot.color,
    linetype: snapshot.linetype,
    lineweight: snapshot.lineweight,
  }));
}

/**
 * Locate the INSERT (`BlockInsertEntity`) with `insertId` across every space
 * and replace it with `{ ...entity, ...buildPatch(entity) }`, then
 * re-assemble the document via the fast in-place path (`[insertId]` — id/
 * spaceId preserved by construction, no entity added/removed). The space-only
 * half of `patchInsertAnywhere`'s two-branch resolution above (round-4 —
 * both `patchInsertAttributes` and `restoreInsertAttributesVerbatim` now
 * route through `patchInsertAnywhere`, which falls back to this for the
 * common "still in a live space" case and to the nested block-definition
 * write path otherwise).
 */
function patchInsertEntityInPlace(
  document: DrawingDocument,
  insertId: string,
  buildPatch: (
    entity: Extract<BlockInsertEntity, { type: "Block" }>,
  ) => Partial<BlockInsertEntity>,
): DrawingDocument {
  return assembleDrawingDocumentInPlace(
    {
      ...document,
      spaces: document.spaces.map((space) => ({
        ...space,
        entities: space.entities.map((entity) => {
          if (entity.id !== insertId || entity.type !== "Block") return entity;
          return { ...entity, ...buildPatch(entity) };
        }),
      })),
    },
    [insertId],
  );
}

/**
 * Internal payload the store's undo-inverse builder attaches to a
 * system-generated `geometry/update-attribute-values` inverse command
 * (Lane F round-? FINDING 2). Mirrors the `__replayAttdef` internal-field
 * precedent for `geometry/create-attdef` redo above — checked first in the
 * handler, bypasses the ordinary tag-matching authoring path entirely.
 */
export interface RestoreAttributesSnapshot {
  attributes: BlockInsertEntity["attributes"];
  layer: string;
  color: EntityColor;
  linetype: string;
  lineweight: string;
}

/**
 * Apply an attributes-array patch to an INSERT in the document. When
 * `properties` is provided, the common layer/color/linetype/lineweight
 * fields are updated too (EATTEDIT Properties tab).
 *
 * Round-4 FINDING 2 — routes through `patchInsertAnywhere` (space OR nested
 * block-definition child) instead of the space-only `patchInsertEntityInPlace`
 * fast path, so a REDO of `geometry/update-attribute-value(s)` targeting an
 * INSERT that has since moved into `blocks[name].entities` (its owning BEDIT
 * session saved/exited between the original edit and the redo) still writes
 * the patch instead of silently no-op'ing.
 */
function patchInsertAttributes(
  document: DrawingDocument,
  insertId: string,
  attributes: BlockInsertEntity["attributes"],
  properties?: {
    layer?: string;
    color?: string;
    linetype?: string;
    lineweight?: string;
  },
): DrawingDocument {
  // T6c incremental validation: DDATTE / ATTEDIT / EATTEDIT edit ONE existing
  // INSERT's `attributes` (+ optional common props) in place — no entity
  // added/removed, id/spaceId preserved via `...entity` → invariants preserved
  // by construction → fast-path with this INSERT's own id. (The explicit
  // `coerceColor` above already types the colour before parse — the dev/test
  // per-entity `drawingEntitySchema.parse` still runs the entity-level color
  // preprocess, and PROD skips parse exactly as the full path did.)
  return patchInsertAnywhere(document, insertId, () => ({
    attributes,
    ...(properties?.layer !== undefined ? { layer: properties.layer } : {}),
    ...(properties?.color !== undefined
      ? // EPIC-COLOR-UNION S1 — coerce the EATTEDIT string into the typed
        // `EntityColor` union before it lands on the entity (assembleDrawing
        // Document does NOT re-parse the schema in PROD, so the preprocess
        // would not run — coerce explicitly here).
        { color: coerceColor(properties.color) }
      : {}),
    ...(properties?.linetype !== undefined
      ? { linetype: properties.linetype }
      : {}),
    ...(properties?.lineweight !== undefined
      ? { lineweight: properties.lineweight }
      : {}),
  }));
}

export function handleUpdateAttributeValue(
  document: DrawingDocument,
  command: UpdateAttributeValueCommand,
): DrawingDocument {
  // Round-4 FINDING 2 — `resolveInsertAnywhere` (space OR nested
  // block-definition child) instead of the space-only `findInsertById`, so a
  // REDO of this command after its owning BEDIT session has been
  // saved/exited (the INSERT survives only inside `blocks[name].entities`)
  // still finds the target instead of silently no-op'ing while consuming
  // the redo history slot.
  const insert = resolveInsertAnywhere(document, command.insertId);
  if (!insert) return document;
  const blockDef = document.blocks[insert.blockName];
  if (!blockDef) return document;

  let nextAttributes: BlockInsertEntity["attributes"];
  try {
    const result = updateAttributeValueMutation({
      insert,
      blockDefinition: blockDef,
      tag: command.tag,
      ...(command.newValue !== undefined ? { newValue: command.newValue } : {}),
      ...(command.overrides ? { overrides: command.overrides } : {}),
    });
    nextAttributes = result.attributes;
  } catch {
    // Editing a constant or missing tag — App.tsx surfaces the toast.
    return document;
  }

  return patchInsertAttributes(document, command.insertId, nextAttributes);
}

export function handleUpdateAttributeValues(
  document: DrawingDocument,
  command: UpdateAttributeValuesCommand,
): DrawingDocument {
  // Lane F round-? FINDING 2 — system-generated undo inverse. Checked FIRST,
  // exactly like `__replayAttdef` above: when present, bypass the ordinary
  // tag-matching authoring path and restore the pre-mutation snapshot
  // verbatim (module doc on `restoreInsertAttributesVerbatim`).
  //
  // Round-3 FINDING 2 — the presence check used to be a space-only
  // `findInsertById` gate that returned `document` unchanged (a no-op,
  // still consuming the history slot) whenever the target INSERT had
  // moved into a block definition (BEDIT saved/exited between the forward
  // edit and the undo). `restoreInsertAttributesVerbatim` now resolves the
  // location itself (space OR block-definition child) — call it directly
  // and let IT decide when the target is genuinely unreachable.
  const restore = (
    command as UpdateAttributeValuesCommand & {
      __restoreAttributes?: RestoreAttributesSnapshot;
    }
  ).__restoreAttributes;
  if (restore) {
    return restoreInsertAttributesVerbatim(
      document,
      command.insertId,
      restore,
    );
  }

  // Round-4 FINDING 2 — same block-definition-aware fallback as
  // `handleUpdateAttributeValue` above (see its comment) for a REDO of the
  // batch EATTEDIT command.
  const insert = resolveInsertAnywhere(document, command.insertId);
  if (!insert) return document;
  const blockDef = document.blocks[insert.blockName];
  if (!blockDef) return document;

  let nextAttributes: BlockInsertEntity["attributes"];
  try {
    const result = updateAttributeValuesMutation({
      insert,
      blockDefinition: blockDef,
      updates: command.updates,
    });
    nextAttributes = result.attributes;
  } catch {
    return document;
  }

  return patchInsertAttributes(
    document,
    command.insertId,
    nextAttributes,
    command.insertProperties,
  );
}

// ═══════════════════════════════════════════════════════════════
// BATTMAN handlers — remove/update/rename-tag/reorder ATTDEF (Phase 2.4)
// ═══════════════════════════════════════════════════════════════

export function handleRemoveAttdef(
  document: DrawingDocument,
  command: RemoveAttdefCommand,
): DrawingDocument {
  try {
    const result = removeAttdefMutation({
      blockName: command.blockName,
      attdefId: command.attdefId,
      removeFromInserts: command.removeFromInserts,
      drawingDocument: document,
    });
    return assembleDrawingDocument(result.updatedDocument);
  } catch {
    return document;
  }
}

export function handleUpdateAttdef(
  document: DrawingDocument,
  command: UpdateAttdefCommand,
): DrawingDocument {
  try {
    const result = updateAttdefMutation({
      blockName: command.blockName,
      attdefId: command.attdefId,
      updates: command.updates,
      drawingDocument: document,
    });
    return assembleDrawingDocument(result.updatedDocument);
  } catch {
    return document;
  }
}

export function handleRenameAttdefTag(
  document: DrawingDocument,
  command: RenameAttdefTagCommand,
): DrawingDocument {
  try {
    const result = renameAttdefTagMutation({
      blockName: command.blockName,
      attdefId: command.attdefId,
      newTag: command.newTag,
      drawingDocument: document,
    });
    return assembleDrawingDocument(result.updatedDocument);
  } catch {
    return document;
  }
}

export function handleReorderAttdefs(
  document: DrawingDocument,
  command: ReorderAttdefsCommand,
): DrawingDocument {
  try {
    const result = reorderAttdefsMutation({
      blockName: command.blockName,
      attdefIds: command.attdefIds,
      drawingDocument: document,
    });
    return assembleDrawingDocument(result.updatedDocument);
  } catch {
    return document;
  }
}

// ═══════════════════════════════════════════════════════════════
// ATTSYNC handler (Phase 2.5)
// ═══════════════════════════════════════════════════════════════

export function handleAttsync(
  document: DrawingDocument,
  command: AttsyncCommand,
): DrawingDocument {
  try {
    const result = attsyncMutation({
      blockName: command.blockName,
      drawingDocument: document,
      ...(command.targetInsertIds
        ? { targetInsertIds: command.targetInsertIds }
        : {}),
    });
    return assembleDrawingDocument(result.updatedDocument);
  } catch {
    return document;
  }
}
